package ai.jessie.web.pages.organisations

import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.homeSideItems
import ai.jessie.web.theme.themedButton
import ai.jessie.web.work.LocalWorkPort
import ai.jessie.web.work.WorkState
import ai.jessie.web.work.calendarDateLabel
import ai.jessie.web.work.calendarWeekday
import ai.jessie.web.work.validCalendarDate
import ai.jessie.web.work.workRecords
import ai.jessie.web.work.workReferences
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

// UC-008. Tenant state is keyed by the routed organisation, so a change of organisation discards
// it together with any unsaved work entry.
@Page("{organisationId}/work")
@Composable
fun organisationWorkPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        appFrame(
            organisationId,
            "/organisations/$organisationId/work",
            "Meine Stunden",
            side = homeSideItems(organisationId),
        ) {
            Div(attrs = AlertStyle.toAttrs()) {
                onboardingProblem(organisationRequiredProblem(), busy = false)
            }
        }
    } else key(organisationId) { workContent(organisationId, null) }
}

@Composable
internal fun workContent(organisationId: String, workId: String?) {
    val port = LocalWorkPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, workId) {
            WorkState(port, scope, organisationId, workId)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    val state = model.state
    val authenticationRequired = state.error?.authenticationRequired == true
    // The entry page is titled by the returned entry; the list keeps its name because the list
    // call has no range.
    val entry = state.current?.takeIf { workId != null && validCalendarDate(it.workDate) }
    val references = entry?.let { workReferences(it, state.selections) }
    val creatable =
        workId == null && state.entries != null && state.current == null && !authenticationRequired
    appFrame(
        organisationId,
        "/organisations/$organisationId/work",
        when {
            workId == null -> "Meine Stunden"
            entry != null -> calendarDateLabel(entry.workDate)
            else -> "Arbeit"
        },
        kicker = entry?.let { calendarWeekday(it.workDate) },
        subtitle = references?.let { "${it.project} · ${it.position}" },
        authenticationRequired = authenticationRequired,
        side = homeSideItems(organisationId),
        actions =
            if (creatable) {
                {
                    themedButton(
                        "Neue Arbeit",
                        model.actions.create,
                        variant = ButtonVariant.PRIMARY,
                        disabled = state.busy,
                    )
                }
            } else null,
    ) {
        workRecords(organisationId, workId, state, model.actions)
    }
}
